import { useState, useEffect } from 'react';
import { Button, Card, Form, Spinner } from 'react-bootstrap';
import { BoxArrowUpRight } from 'react-bootstrap-icons';
import { Step, StepList, StepResult, type StepStatus } from '@/components/config/StepList';
import { SystemUserSecret } from '@/components/config/SystemUserSecret';
import { ToolPage } from '@/components/ui/ToolPage';
import { nextgenBrokerService } from '@/services/nextgenBrokerService';
import { validateClientName, sanitizeClientName } from '@/utils/clientValidation';

export default function SetupNonNextgenClient() {
  const [accountId, setAccountId] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientIdError, setClientIdError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [assumedRoleArn, setAssumedRoleArn] = useState<string | null>(null);
  // Each step keeps its own error, so a failure shows under the step that failed.
  const [checkErr, setCheckErr] = useState<string | null>(null);
  const [secretErr, setSecretErr] = useState<string | null>(null);
  const [fetching, setFetching] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);
  // Steps 2 and 3 happen on other pages. The page knows only that it sent the
  // person there, so it marks those steps as opened, never as done.
  const [configOpened, setConfigOpened] = useState(false);
  const [deployOpened, setDeployOpened] = useState(false);

  // Validate client ID whenever it changes
  useEffect(() => {
    if (clientId.trim().length > 0) {
      const error = validateClientName(clientId);
      setClientIdError(error);
    } else {
      setClientIdError(null);
    }
  }, [clientId]);

  const precheck = async () => {
    setAssumedRoleArn(null);
    setCheckErr(null);
    try {
      setChecking(true);
      const res = await nextgenBrokerService.precheckAssumeClientRole(accountId.trim());
      setAssumedRoleArn(res.assumedRoleArn);
    } catch (e) {
      setCheckErr(e instanceof Error ? e.message : 'The account did not answer.');
    } finally {
      setChecking(false);
    }
  };

  const onRetrieve = async () => {
    setSecretErr(null);
    setSecret(null);
    try {
      setFetching(true);
      // The broker reads the account and region from the config saved in step 2.
      const res = await nextgenBrokerService.getSystemUserSecret(clientId.trim());
      setSecret(res.secretString || '');
    } catch (e) {
      setSecretErr(e instanceof Error ? e.message : 'The account did not answer.');
    } finally {
      setFetching(false);
    }
  };

  const name = clientId.trim();
  const account = accountId.trim();
  const role = 'ArcanumAIAccess';
  const nameUsable = !!name && !clientIdError;
  const suggestion = sanitizeClientName(clientId);

  const openCreateConfig = () => {
    const params = new URLSearchParams();
    if (nameUsable) params.set('clientName', name);
    params.set('accountId', account);
    // The account is the customer's own, so the contract starts on Own account.
    params.set('org', 'standalone');
    window.open(`/tools/create-client-config?${params.toString()}`, '_blank', 'noreferrer');
    setConfigOpened(true);
  };

  const openUpdateConfig = () => {
    const query = nameUsable ? `?clientName=${encodeURIComponent(name)}` : '';
    window.open(`/tools/update-client-config${query}`, '_blank', 'noreferrer');
    setConfigOpened(true);
  };

  const openDeployments = () => {
    window.open('/deployments', '_blank', 'noreferrer');
    setDeployOpened(true);
  };

  // The current step is the first one the page has not seen done or opened.
  const passed = [!!assumedRoleArn, configOpened, deployOpened, secret !== null];
  const firstOpen = passed.indexOf(false);
  const currentStep = firstOpen === -1 ? 5 : firstOpen + 1;
  const statusOf = (step: number, verified: boolean): StepStatus => {
    if (step === currentStep) return 'current';
    if (!passed[step - 1]) return 'todo';
    return verified ? 'done' : 'started';
  };
  // Only the current step carries the primary button.
  const variantOf = (step: number) => (step === currentStep ? 'primary' : 'outline-secondary');

  return (
    <ToolPage
      title="Set up a non-NextGen client"
      subtitle="Check that Arcanum can get into the client's own AWS account, write its config and run the first deploy."
      width="narrow"
    >
      <Card className="mb-3">
        <Card.Body>
          <h2 className="section-label">The client</h2>
          <Form.Group className="mb-3" controlId="non-nextgen-account-id">
            <Form.Label>AWS account ID</Form.Label>
            <Form.Control
              className="mono"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              placeholder="123456789012"
              inputMode="numeric"
            />
            <Form.Text>The 12-digit ID of the client's own AWS account.</Form.Text>
          </Form.Group>
          <Form.Group controlId="non-nextgen-client-name">
            <Form.Label>Client name</Form.Label>
            <Form.Control
              className="mono"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder="arcanum-demo"
              isInvalid={!!clientIdError}
            />
            {clientIdError ? (
              <>
                <Form.Control.Feedback type="invalid">{clientIdError}</Form.Control.Feedback>
                {suggestion && suggestion !== clientId && (
                  <Form.Text>
                    Try <span className="mono">{suggestion}</span>.
                  </Form.Text>
                )}
              </>
            ) : (
              <Form.Text>
                Lowercase letters, numbers and dashes. It fills in the config form and makes the client's web address.
              </Form.Text>
            )}
          </Form.Group>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <StepList>
            <Step
              number={1}
              title="Check access"
              status={statusOf(1, true)}
              description={
                <>
                  Tries to sign in to account <span className="mono">{account || 'the account'}</span> through the role{' '}
                  <span className="mono">{role}</span>. Nothing in the account changes.
                </>
              }
            >
              <div className="step-list__actions">
                <Button variant={variantOf(1)} onClick={precheck} disabled={!accountId || checking}>
                  {checking ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Checking
                    </>
                  ) : (
                    'Check access'
                  )}
                </Button>
              </div>
              {assumedRoleArn && (
                <StepResult tone="success">
                  Access works. Signed in through <span className="mono">{assumedRoleArn}</span>.
                </StepResult>
              )}
              {checkErr && (
                <StepResult tone="danger">
                  <strong>Arcanum cannot get into this account.</strong> {checkErr} Ask the client to check that the
                  role exists and that it trusts Arcanum.
                </StepResult>
              )}
            </Step>

            <Step
              number={2}
              title="Write the config"
              status={statusOf(2, false)}
              description="Opens the config form in a new tab with the client name and the account filled in. Use Update if the client already has a config."
            >
              <div className="step-list__actions">
                <Button variant={variantOf(2)} onClick={openCreateConfig}>
                  Create config
                  <BoxArrowUpRight className="ms-2" aria-hidden="true" />
                </Button>
                <Button variant="outline-secondary" onClick={openUpdateConfig}>
                  Update config
                  <BoxArrowUpRight className="ms-2" aria-hidden="true" />
                </Button>
              </div>
              {configOpened && (
                <StepResult tone="muted">Opened in a new tab. Come back here when the config is saved.</StepResult>
              )}
            </Step>

            <Step
              number={3}
              title="Run the first deploy"
              status={statusOf(3, false)}
              description={
                <>
                  Opens Deployments in a new tab. Start a deployment for{' '}
                  <span className="mono">{name || 'the client'}</span> there and watch its log until it finishes.
                </>
              }
            >
              <div className="step-list__actions">
                <Button variant={variantOf(3)} onClick={openDeployments}>
                  Open Deployments
                  <BoxArrowUpRight className="ms-2" aria-hidden="true" />
                </Button>
              </div>
              {deployOpened && (
                <StepResult tone="muted">Opened in a new tab. Come back here when the deploy has finished.</StepResult>
              )}
            </Step>

            <Step
              number={4}
              title="Get the system user password"
              status={statusOf(4, true)}
              description="Reads the sign-in the deploy made for the system user, from the account in the client's config. It exists only after the first deploy finishes. Nothing in the account changes."
            >
              <div className="step-list__actions">
                <Button variant={variantOf(4)} onClick={onRetrieve} disabled={!nameUsable || fetching}>
                  {fetching ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Getting the password
                    </>
                  ) : (
                    'Get password'
                  )}
                </Button>
              </div>
              {secret !== null && (
                <div className="mt-3">
                  <SystemUserSecret raw={secret} />
                </div>
              )}
              {secretErr && (
                <StepResult tone="danger">
                  <strong>Could not get the password.</strong> {secretErr} Check that the config is saved and that the
                  first deploy has finished.
                </StepResult>
              )}
            </Step>

            <Step
              number={5}
              title="Open the workspace"
              status={statusOf(5, false)}
              description="The client's Numa address, made from the client name. It works once the first deploy has finished. Sign in as the system user."
            >
              {name ? (
                <a
                  href={`https://${name}.numa.arcanum.ai`}
                  target="_blank"
                  rel="noreferrer"
                  className="step-list__link"
                >
                  https://{name}.numa.arcanum.ai
                </a>
              ) : (
                <span className="tool-form__hint">Enter a client name above to get the address.</span>
              )}
            </Step>
          </StepList>
        </Card.Body>
      </Card>
    </ToolPage>
  );
}
